import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react'

import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { UseSubmitAssessmentData, useSubmitAssessments } from 'queries/user'
import {
  AssessmentWorkflowState,
  useGlobalWorkflowsContextValue,
} from '../../GlobalWorkflowManager/GlobalWorkflowManager'

export type AssessmentFormData = {
  assessments: UseSubmitAssessmentData
  evaluationNote?: string | null
}

type AssessRequirementsWorkflowProviderContextState = {
  actions: {
    submitFormToServer: () => Promise<unknown>
    setFormData: React.Dispatch<React.SetStateAction<AssessmentFormData>>
    close: () => void
  }
  state: {
    isOpen: boolean
    data: AssessmentWorkflowState
    formData: AssessmentFormData
    mutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
}

const [Context, useAssessRequirementsWorkflowContextValue] =
  createContext<AssessRequirementsWorkflowProviderContextState>({
    name: 'assessmentWorkflowContext',
  })

type AssessmentWorkflowContextProvderProps = PropsWithChildren<{
  initialData: AssessmentWorkflowState
  isOpen: boolean
  close: () => void
}>

const defaultFormData: AssessmentFormData = {
  assessments: {},
  evaluationNote: '',
}

const AssessRequirementsWorkflowContextProvider = ({
  children,
  initialData,
  isOpen,
  close,
}: AssessmentWorkflowContextProvderProps) => {
  const [formData, setFormData] = useState<AssessmentFormData>(defaultFormData)

  const {
    mutateAsync: submitAssessment,
    isPending,
    isError,
    error,
  } = useSubmitAssessments({
    userId: initialData.worker.id,
    shiftId: initialData.shiftId,
    externalCandidateId:
      initialData.assessments.workerAssessment.interview.externalCandidateId,
  })

  useEffect(() => {
    const { workerAssessment, requirements } = initialData.assessments
    if (!workerAssessment?.interview?.externalCandidateId) {
      setFormData(defaultFormData)
      return
    }
    const initialAssessments = requirements.reduce(
      (acc, { workerRequirementId }) => {
        const evaluation = workerAssessment.evaluations[workerRequirementId]
        if (
          evaluation?.adminStatus &&
          ['pass', 'fail'].includes(evaluation.adminStatus)
        ) {
          acc[workerRequirementId] = {
            workerRequirementId,
            status: evaluation.adminStatus,
          }
        }
        return acc
      },
      {} as UseSubmitAssessmentData
    )
    setFormData({
      assessments: initialAssessments,
      evaluationNote: workerAssessment.evaluationNote ?? null,
    })
  }, [
    initialData.worker.id,
    initialData.assessments.workerAssessment?.interview?.externalCandidateId,
    initialData.assessments,
  ])

  const submitFormToServer = useCallback(async () => {
    await submitAssessment(formData)
  }, [formData, submitAssessment])

  const value: AssessRequirementsWorkflowProviderContextState = {
    actions: {
      submitFormToServer,
      setFormData,
      close,
    },
    state: {
      data: initialData,
      isOpen,
      formData,
      mutationState: {
        isPending,
        isError,
        errorMessage: parseErrorMessage(error),
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const AssessRequirementsWorkflowProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    assessmentWorkflow: { isOpen, close, data },
  } = useGlobalWorkflowsContextValue()

  const baseId = useId()

  if (!isOpen || !data) {
    return null
  }

  return (
    <AssessRequirementsWorkflowContextProvider
      initialData={data}
      // resets the initial state when the modal is opened or closed
      key={`${baseId}-${isOpen}`}
      isOpen={isOpen}
      close={close}
    >
      {children}
    </AssessRequirementsWorkflowContextProvider>
  )
}

export { useAssessRequirementsWorkflowContextValue }
